import { api } from './client';
import type {
  Memory,
  MemoryListQuery,
  MemoryPage,
  MemoryRecallResult,
  MemoryRelevance,
  MemoryStats,
  MemoryTenantPage,
  MemoryTenantsQuery,
} from '../data/types';

/** The memory routes (DR-75). `router_memories` is closed to browsers — the
 *  text is tenant content written by agents — so this is the only way the
 *  console sees a memory: projected field by field, tenant-scoped, the
 *  tuple checked on every read. Every path segment is encoded; nothing a
 *  memory contains is ever interpolated into a URL. */

const ADMIN = '/api/v1/admin';

function base(estateId: string, tenantId: string): string {
  return `${ADMIN}/estates/${encodeURIComponent(estateId)}/tenants/${encodeURIComponent(tenantId)}/memory`;
}

/** listMemories pages a tenant's memories. A POST, so an operator's filter
 *  text stays out of request logs. */
export async function listMemories(q: MemoryListQuery): Promise<MemoryPage> {
  const res = await api.post<MemoryPage>(`${base(q.estateId, q.tenantId)}/list`, {
    scope: q.scope || undefined,
    kind: q.kind || undefined,
    status: q.status || undefined,
    tag: q.tag || undefined,
    text: q.text || undefined,
    limit: q.limit,
    cursor: q.cursor || undefined,
  });
  return { items: res.items ?? [], nextCursor: res.nextCursor };
}

export function getMemory(estateId: string, tenantId: string, id: string): Promise<Memory> {
  return api.get<Memory>(`${base(estateId, tenantId)}/${encodeURIComponent(id)}`);
}

export function memoryStats(estateId: string, tenantId: string): Promise<MemoryStats> {
  return api.get<MemoryStats>(`${base(estateId, tenantId)}/stats`);
}

/** memoryTenants pages the tenants of one estate that hold memories, with
 *  each one's counts (DR-86) — the one memory read that spans tenants, and
 *  it carries no memory, only ids and counts. */
export async function memoryTenants(q: MemoryTenantsQuery): Promise<MemoryTenantPage> {
  const params = new URLSearchParams({ limit: String(q.limit) });
  if (q.cursor) params.set('cursor', q.cursor);
  const res = await api.get<MemoryTenantPage>(`${ADMIN}/estates/${encodeURIComponent(q.estateId)}/memory/tenants?${params}`);
  return { items: res.items ?? [], nextCursor: res.nextCursor };
}

/** recallMemories runs the data plane's own recall and returns its evidence.
 *  It spends one of the index's search requests and writes nothing. */
export function recallMemories(
  estateId: string,
  tenantId: string,
  q: { query: string; scopes?: string[]; k?: number; relevance?: MemoryRelevance },
): Promise<MemoryRecallResult> {
  return api.post<MemoryRecallResult>(`${base(estateId, tenantId)}/recall`, {
    query: q.query,
    scopes: q.scopes?.length ? q.scopes : undefined,
    k: q.k || undefined,
    relevance: q.relevance || undefined,
  });
}

/** forgetMemory tombstones one memory — the one mutation a browser may
 *  perform. The row comes back as it now is. */
export function forgetMemory(estateId: string, tenantId: string, id: string): Promise<Memory> {
  return api.del<Memory>(`${base(estateId, tenantId)}/${encodeURIComponent(id)}`);
}
